/**
 * modStatusMeta.js
 *
 * Stores the order status at the moment a modification was STARTED, keyed by
 * orderId + modificationCycle. This persists across Redux re-fetches and app
 * restarts so `getOrderDisplayStatus` can correctly classify a modification as
 * post-billed even after the order has been re-billed (which overwrites billedAt
 * to a timestamp newer than the modification).
 *
 * Storage: localStorage key 'pos_mod_status_meta'
 * Format:  { "orderId:modCycle": "billed" | "confirmed" | ... }
 */

const STORAGE_KEY = 'pos_mod_status_meta';
const MAX_ENTRIES = 500; // prune oldest when exceeded

/**
 * Save the originalStatus for a specific order modification.
 * Call this when `finishOrderModification` completes (both online & offline).
 */
export function saveModOriginalStatus(orderId, modCycle, originalStatus) {
  if (!orderId || modCycle == null || !originalStatus) return;
  try {
    const map   = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    const key   = `${orderId}:${modCycle}`;
    map[key]    = originalStatus;

    // Prune oldest entries to stay under MAX_ENTRIES
    const keys = Object.keys(map);
    if (keys.length > MAX_ENTRIES) {
      keys.slice(0, keys.length - MAX_ENTRIES).forEach(k => delete map[k]);
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch { /* non-fatal */ }
}

/**
 * Retrieve the stored originalStatus for a specific order modification.
 * Returns null if not found.
 */
export function getModOriginalStatus(orderId, modCycle) {
  if (!orderId || modCycle == null) return null;
  try {
    const map = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    return map[`${orderId}:${modCycle}`] || null;
  } catch { return null; }
}
